// crewbit-admin/src/hooks/useOutstandingInvoices.ts
//
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §Outstanding.6 (B-N2).
// Mirrors crewbit-mobile/hooks/useOutstandingInvoices.ts. Web runtime uses
// the Page Visibility API + window events instead of useFocusEffect /
// DeviceEventEmitter.
//
// Naming note: spec uses "useOutstanding" but admin already has a legacy hook
// of that name (useOutstanding.ts — combined Owed/Owe aggregate, used by
// FinancialsPage). To avoid the collision without touching pages/, both
// platforms ship this hook as `useOutstandingInvoices`. The legacy hook
// retires alongside FinancialsPage in a later session.
//
// Refresh policy mirrors useFinancialSummary (post-Session-119 retry-loop fix):
// dataRef pattern keeps doFetch identity stable; deps are [activeBusinessId]
// only.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { bucketForDays, type AgingBucket } from '../lib/aging';
import {
  useActiveBusinessStore,
  ACTIVE_BUSINESS_CHANGED_EVENT,
} from './useActiveBusiness';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

export interface OutstandingInvoiceRow {
  id: string;
  invoice_number: string;
  client_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  total: number;
  status: 'sent' | 'viewed' | 'overdue';
  due_date: string | null;
  days_outstanding: number;
  bucket: AgingBucket;
  last_reminder_sent_at: string | null;
  sent_via: string | null;
  share_token: string | null;
}

export interface UseOutstandingInvoicesReturn {
  data: OutstandingInvoiceRow[] | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  requiresBusinessSelection: boolean;
  refetch: () => Promise<void>;
}

interface RawRow {
  id: string;
  invoice_number: string;
  client_id: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  total: number;
  status: string;
  due_date: string | null;
  created_at: string | null;
  last_reminder_sent_at: string | null;
  sent_via: string | null;
  share_token: string | null;
}

function normalizeRow(raw: RawRow, today: Date): OutstandingInvoiceRow {
  const referenceDateStr =
    raw.due_date ?? (raw.created_at ? raw.created_at.slice(0, 10) : null);
  const refDate = referenceDateStr ? new Date(referenceDateStr + 'T00:00:00') : today;
  const days = Math.max(0, Math.floor((today.getTime() - refDate.getTime()) / 86_400_000));
  return {
    id: raw.id,
    invoice_number: raw.invoice_number,
    client_id: raw.client_id,
    client_name: raw.client_name,
    client_email: raw.client_email,
    client_phone: raw.client_phone,
    total: raw.total,
    status: raw.status as OutstandingInvoiceRow['status'],
    due_date: raw.due_date,
    days_outstanding: days,
    bucket: bucketForDays(days),
    last_reminder_sent_at: raw.last_reminder_sent_at,
    sent_via: raw.sent_via,
    share_token: raw.share_token,
  };
}

export function useOutstandingInvoices(): UseOutstandingInvoicesReturn {
  const activeBusinessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const requiresSelection = useActiveBusinessStore((s) => s.requiresSelection);

  const [data, setData] = useState<OutstandingInvoiceRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<OutstandingInvoiceRow[] | null>(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!activeBusinessId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const { data: rows, error: queryError } = await supabase
          .from('invoices')
          .select(
            'id, invoice_number, client_id, client_name, client_email, client_phone, ' +
              'total, status, created_at, due_date, last_reminder_sent_at, sent_via, share_token',
          )
          .eq('business_id', activeBusinessId)
          .in('status', ['sent', 'viewed', 'overdue'])
          .eq('is_disputed', false)
          .order('due_date', { ascending: true, nullsFirst: false });

        if (!mountedRef.current) return;

        if (queryError) {
          setError(new Error(queryError.message));
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          setData((rows ?? []).map((r) => normalizeRow(r as unknown as RawRow, today)));
          setError(null);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [activeBusinessId],
  );

  // Reset on tenant change.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [activeBusinessId]);

  // Mount fetch.
  useEffect(() => {
    if (!activeBusinessId) return;
    void doFetch(false);
  }, [activeBusinessId, doFetch]);

  // Page Visibility focus refetch.
  useEffect(() => {
    if (!activeBusinessId) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, [activeBusinessId, doFetch]);

  // Cache invalidation on Money mutations + active-business changes.
  useEffect(() => {
    const onMutation = () => { void doFetch(true); };
    const onBusinessChange = () => { /* dependency-change effect handles refetch */ };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    window.addEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, onBusinessChange);
    return () => {
      window.removeEventListener(MONEY_MUTATION_EVENT, onMutation);
      window.removeEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, onBusinessChange);
    };
  }, [doFetch]);

  const refetch = useCallback(async () => { await doFetch(true); }, [doFetch]);

  return useMemo(
    () => ({
      data,
      isLoading,
      isRefreshing,
      error,
      requiresBusinessSelection: requiresSelection,
      refetch,
    }),
    [data, isLoading, isRefreshing, error, requiresSelection, refetch],
  );
}
